import { Button, Checkbox } from "antd";
import { useEffect, useState } from "react";
import ConfirmModal from "../../../../components/ui/ConfirmModal";
import CustomModal from "../../../../components/ui/CustomModal";
import { usePettyCashSendMutation } from "../../../../features/pettyCash/pettyCash";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import { toast } from "../../../../lib/toast";
import { ModalComponentProps } from "../../../../types";
import { formatSouthAsianWithDecimals } from "../../../../utils/utils";
import WarningIcon from "../_icons/WarningIcon";
import { SelectedDataType } from "../PettyCash";

export default function SendBulkDisbursement({
  handleClose,
  data,
  ...rest
}: ModalComponentProps<SelectedDataType>) {
  const [agree, setAgree] = useState<boolean>(false);
  const [pettyCashSend, { isLoading, isError, error, isSuccess }] =
    usePettyCashSendMutation();
  useError(isError, error, "Send bulk disbursement fail");
  const {
    handleOpen,
    open,
    handleClose: downloadClose,
  } = useModal<undefined, "DOWNLOAD">();

  const amount = data?.reduce((acc, curr) => acc + Number(curr?.amount), 0);
  const ids = data?.map((item) => item?.id);
  const expireList = data?.filter((el) => el?.isExpire);

  useEffect(() => {
    if (isSuccess) {
      toast("success", "Bulk disbursement sent successfully");
      handleClose(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  const handleDownload = () => {
    const data = open?.data;

    // Format into readable text
    const content = (data as unknown as any[])
      ?.map(
        (item) =>
          `Name: ${item.employeeName}\nEmail: ${
            item.employeeEmail
          }\nMissing Fields:\n- ${item.missingFields.join("\n- ")}\n`
      )
      .join("\n----------------------\n\n");

    // Create and download file
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "error-log.txt";
    link.click();
    URL.revokeObjectURL(url);
    handleClose(true);
  };

  useEffect(() => {
    if (
      isError &&
      (error as { data: { data: any } })?.data?.data?.employeesWithMissingInfo
        ?.length
    ) {
      handleOpen({
        type: "DOWNLOAD",
        data: (error as { data: { data: any } })?.data?.data
          ?.employeesWithMissingInfo,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, error]);

  return (
    <CustomModal
      title=""
      footer={null}
      width={400}
      closeIcon={null}
      handleClose={handleClose}
      {...rest}
    >
      {open.type === "DOWNLOAD" && (
        <ConfirmModal
          open={open?.open}
          handleClose={downloadClose}
          handleOk={handleDownload}
          confirmTitle="Error Detected"
          questionText={(error as { data: { message: string } })?.data?.message}
          okText="Download"
        />
      )}
      <WarningIcon />
      <h6 className="text-zinc-700 text-lg font-semibold mt-2.5">
        {expireList?.length ? (
          <span>
            Due date has already passed for {expireList?.length} petty cash{" "}
            {`request${expireList?.length > 1 ? "s" : ""}`}. Are you sure you
            want to send all {data?.length}{" "}
            {`request${Number(data?.length) > 1 ? "s" : ""}`} for disbursement?
          </span>
        ) : (
          <>
            Are you sure you want to send {data?.length}{" "}
            {`request${Number(data?.length) > 1 ? "s" : ""}`} for disbursement?
          </>
        )}
        <br />
        Total Amount:{" "}
        <span className="text-violet-500">
          ৳{formatSouthAsianWithDecimals(Number(amount))}
        </span>
      </h6>

      <div className="my-4">
        <Checkbox
          className="!select-none !font-medium"
          checked={agree}
          onChange={(e) => setAgree(e.target.checked)}
        >
          Notify employee by email
        </Checkbox>
      </div>

      <div className="w-full flex items-center justify-between gap-3">
        <Button
          className="normal-dark-button modal-button"
          onClick={handleClose}
          size="middle"
          block
        >
          Cancel
        </Button>
        <Button
          className="modal-button"
          type="primary"
          htmlType="submit"
          size="middle"
          block
          loading={isLoading}
          onClick={() =>
            pettyCashSend({
              body: {
                petty_cash_ids: ids,
                notify_by_email: agree,
              },
            })
          }
        >
          Send
        </Button>
      </div>
    </CustomModal>
  );
}
